import type { ReactNode } from 'react'
import { useState } from 'react'
import { Badge, Button, cx } from './core.tsx'

// ----------------------------------------------------------------- ListRow

export type ListRowProps = {
  code?: string
  title: ReactNode
  meta?: ReactNode
  selected?: boolean
  onClick?: () => void
  /** Menu de acoes rapidas. Fica fora do clique principal da linha. */
  actions?: ReactNode
}

/**
 * Linha de listagem.
 *
 * Codigo e titulo abrem o detalhe; acoes rapidas vivem no menu a direita
 * (spec 13.2). No mobile o titulo ocupa a linha inteira e o resto quebra
 * embaixo — e isso que transforma a linha em card compacto sem componente novo.
 */
export function ListRow({ code, title, meta, selected, onClick, actions }: ListRowProps) {
  return (
    <div className={cx('ob-row', selected && 'ob-row--selected')} onClick={onClick}>
      {code ? <span className="ob-row__code">{code}</span> : null}
      <span className="ob-row__title">{title}</span>
      {meta ? <span className="ob-row__meta">{meta}</span> : null}
      {actions ? (
        <span className="ob-row__meta" onClick={(e) => e.stopPropagation()}>
          {actions}
        </span>
      ) : null}
    </div>
  )
}

// ------------------------------------------------------------- FilterPanel

export type FilterPanelProps = {
  children: ReactNode
  /** Quantos filtros estao preenchidos — vira contador no mobile. */
  activeCount?: number
  onClear?: () => void
  /** Numero total de campos. Com 4 ou mais o painel recolhe no mobile. */
  fieldCount?: number
}

/**
 * Barra de filtros.
 *
 * Com quatro ou mais campos, o painel recolhe no mobile e mostra o contador de
 * filtros ativos (spec 6) — caso contrario os filtros empurrariam a lista para
 * fora da primeira tela.
 */
export function FilterPanel({ children, activeCount = 0, onClear, fieldCount = 0 }: FilterPanelProps) {
  const [open, setOpen] = useState(false)
  const collapsible = fieldCount >= 4

  return (
    <div
      className={cx('ob-filters', collapsible && 'ob-filters--collapsible', open && 'ob-filters--open')}
      role="search"
    >
      {collapsible ? (
        <Button
          className="ob-filters__toggle"
          icon="fas fa-sliders-h"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          Filtros
          {activeCount > 0 ? (
            <Badge tone="primary">{activeCount}</Badge>
          ) : null}
        </Button>
      ) : null}
      <div className="ob-filters__fields">{children}</div>
      {activeCount > 0 && onClear ? (
        <Button variant="ghost" size="sm" icon="fas fa-times" onClick={onClear}>
          Limpar
        </Button>
      ) : null}
    </div>
  )
}
